/**
 * @file: index.ts
 * @author: eric <eric.blueplus@gmail.com>
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { TimelineItemBuilder } from '@euler/app/flows/task/functions/timeline/types';
import { maskedMobilePhone } from '@euler/app/tv/functions/maskedMobilePhone';
import { StatusColors } from '@euler/components/Colors';
import { FontFamily } from '@euler/components/typography';
import { AttributedText } from '@euler/model/AttributedText';
import {
  InspectionTaskEventSubType,
  InspectionTaskEventType,
} from '@euler/model/enum';
import { parseTagsFromString } from '@euler/utils';
import { formatDate, formatDateTime } from '@euler/utils/formatters';
import { registerEventType } from '../../registry';

const register = registerEventType(InspectionTaskEventType.Creation);

function licensePlateNoCaptured() {
  register(InspectionTaskEventSubType.LicensePlateNoCaptured)((item, data) => {
    item.label(`车牌: ${data[0]}`);
  });
}

function vinCaptured() {
  register(InspectionTaskEventSubType.VinCaptured)((item, data) => {
    item.label(`VIN: ${data[0]}`);
  });
}

function orderCreated() {
  register(InspectionTaskEventSubType.OrderCreated)((item, data) => {
    item.label(`订单号: ${data[0]}`);
    if (data[1]) {
      item.tagline(`服务顾问: ${data[1]}`);
    }
  });
}

function orderCanceled() {
  register(InspectionTaskEventSubType.OrderCanceled)((item, data) => {
    item.label({
      text: '订单已取消',
      color: StatusColors.Warn,
      fontFamily: FontFamily.NotoSans.Medium,
    });
    if (data.note) {
      item.tagline({
        text: `备注: ${data.note}`,
        color: StatusColors.Notice,
      });
    }
  });
}

function orderClosed() {
  registerEventType(InspectionTaskEventType.Completion)(
    InspectionTaskEventSubType.Closed,
  )(item => {
    item.label('服务终止');
  });
}

function orderUpdated() {
  const builder: TimelineItemBuilder = (item, data) => {
    item.label(`更新订单基本信息`);
    if ('licensePlateNo' in data) {
      item.tagline(`车牌号码: ${data.licensePlateNo}`);
    }
    if ('tags' in data) {
      item.tagline(`业务类型: ${parseTagsFromString(data.tags).join('/')}`);
    }
    if ('vin' in data) {
      item.tagline(`车架号码: ${data.vin}`);
    }
    if ('vehicleMileage' in data) {
      item.tagline(`行驶里程: ${data.vehicleMileage}KM`);
    }
    if ('vehicleName' in data) {
      item.tagline(`车型名称: ${data.vehicleName}`);
    }
    if ('customerName' in data) {
      item.tagline(`客户名称: ${data.customerName || '--'}`);
    }
    if ('customerMobile' in data) {
      item.tagline(
        `客户电话: ${maskedMobilePhone(data.customerMobile) || '--'}`,
      );
    }
    if ('insuranceExpiryDate' in data) {
      item.tagline(
        `保险到期: ${
          data.insuranceExpiryDate ? formatDate(data.insuranceExpiryDate) : '--'
        }`,
      );
    }
    if ('estimatedFinishTime' in data) {
      const line: AttributedText[] = [
        { text: `预计完工: ${formatDateTime(data.estimatedFinishTime)}` },
      ];
      if (data.estimatedFinishTimeUpdateReason) {
        line.push({
          text: ` (${data.estimatedFinishTimeUpdateReason})`,
          color: StatusColors.Warn,
        });
      }
      item.tagline(line);
      if (data.estimatedFinishTimeUpdateRemark) {
        item.tagline(`完工备注: ${data.estimatedFinishTimeUpdateRemark}`);
      }
    }
    if ('remark' in data) {
      item.tagline(`订单备注: ${data.remark || '--'}`);
    }
  };
  register(InspectionTaskEventSubType.TaskBasicInfoUpdated)(builder);
  register(InspectionTaskEventSubType.OrderInfoUpdated)(builder);
}

licensePlateNoCaptured();
vinCaptured();
orderCreated();
orderCanceled();
orderClosed();
orderUpdated();
